import { CloudUploadRounded as CloudUploadRoundedIcon } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Stack,
  styled,
  TextField,
  Typography
} from '@mui/material'
import { t } from 'i18next'
import Joi from 'joi'
import { useEffect, useRef, useState } from 'react'

import useFieldValidation from '../../../helpers/fieldValidation'
import { useValidationEffect } from '../../../helpers/hooks'
import { useAuth } from '../../Shared/Contexts/AuthContext'
import { useOrganizations } from '../Contexts/OrganizationsContext'

const createSchema = Joi.object({
  parentOrganization: Joi.object().empty(null).required(),
  timezone: Joi.string().empty(null).required(),
  organizationName: Joi.string().max(64).empty(null).required(),
  vatNumber: Joi.string().max(64).optional().allow(null, '').default(null),
  organizationKey: Joi.string().max(32).empty(null).required(),
  networkServer: Joi.object().empty(null).required(),
  domain: Joi.string().max(64).empty(null).required(),
  profile: Joi.alternatives().conditional('parentOrganization.key', {
    is: 'root',
    then: Joi.object().empty(null).required(),
    otherwise: Joi.object().empty(null)
  }),
  file: Joi.object().allow(null, '').default(null)
})

const editSchema = Joi.object({
  parentOrganization: Joi.object().empty(null),
  timezone: Joi.string().empty(null).required(),
  organizationName: Joi.string().max(64).empty(null).required(),
  vatNumber: Joi.string().max(64).optional().allow(null, '').default(null),
  organizationKey: Joi.string().max(32).empty(null).required(),
  domain: Joi.string().max(64).allow(null, '').default(null).required(),
  networkServer: Joi.string().allow(null, '').default(null),
  profile: Joi.alternatives().conditional('parentOrganization.key', {
    is: Joi.any().valid(null, '', 'root'),
    then: Joi.object().empty(null).required(),
    otherwise: Joi.object().empty(null)
  }),
  file: Joi.object().allow(null, '').default(null)
})

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
})

const OrganizationForm = ({ onChange, organization, readonly = false }) => {
  const {
    useFetchKeySuggestion,
    useFetchTimezones,
    useFetchAllNetworkServers,
    useFetchOrganizationProfiles,
    useFetchOrganizationProfile
  } = useOrganizations()
  const {
    currentUser: { isSuperAdmin, selfOrganizations }
  } = useAuth()

  const [timezones, setTimezones] = useState([])
  const [isTimezonesLoading, setIsTimezonesLoading] = useState(false)
  const [networkServer, setNetworkServer] = useState()
  const [isNetworkServersLoading, setIsNetworkServersLoading] = useState(false)
  const [organizationProfiles, setOrganizationProfiles] = useState([])
  const [organizationProfile, setOrganizationProfile] = useState()
  const [isOrganizationProfilesLoading, setIsOrganizationProfilesLoading] =
    useState(false)
  const [validation, setValidation] = useState({ isValid: false })
  const [networkServerUserOptions, setNetworkServerOptions] = useState([])
  const firstTime = useRef(true)

  const organizationsOptions =
    selfOrganizations && isSuperAdmin
      ? [{
        key: 'root',
        name: 'root'
      }, ...selfOrganizations]
      : selfOrganizations

  useEffect(() => {
    if (!networkServer) {
      return
    }

    setNetworkServerOptions([
      {
        server: networkServer.id,
        user: 'newUser',
        userId: 'newUser'
      },
      {
        server: 'nsproduction',
        user: 'Nessuno',
        userId: 'withoutUser'
      },
      ...networkServer.users.map((u) => ({
        server: networkServer.id,
        user: u.name,
        userId: u.id
      }))
    ])
  }, [networkServer])

  useEffect(() => {
    setIsTimezonesLoading(true)
    setIsNetworkServersLoading(true)
    setIsOrganizationProfilesLoading(true)

    useFetchTimezones()
      .then((res) => setTimezones(res.data))
      .finally(() => setIsTimezonesLoading(false))

    useFetchAllNetworkServers()
      .then((res) => setNetworkServer(res.data))
      .finally(() => setIsNetworkServersLoading(false))

    useFetchOrganizationProfiles()
      .then((res) => setOrganizationProfiles(res.data))
      .finally(() => setIsOrganizationProfilesLoading(false))

    if (!!organization && organization.orgProfileId) {
      useFetchOrganizationProfile(organization.orgProfileId).then((res) =>
        setOrganizationProfile(res.data)
      )
    }
  }, [])

  const [organizationData, setOrganizationData] = useState({
    organizationName: ''
  })

  useEffect(() => {
    setOrganizationData({
      parentOrganization: organization
        ? organization.parentOrganization || { key: 'root' }
        : null,
      timezone: organization?.timeZone || null,
      organizationName: organization?.name || '',
      vatNumber: organization?.vatNumber || '',
      organizationKey: organization?.key || '',
      domain: organization?.domain || '',
      networkServer: organization?.sourceData?.user,
      file: organization?.file || null,
      profile: organizationProfile
    })
  }, [organizationProfile])

  useValidationEffect(() => {
    const newValidation = useFieldValidation(
      organizationData,
      organization ? editSchema : createSchema
    )

    setValidation(newValidation)
    onChange(organizationData, newValidation.isValid)
  }, [organizationData])

  const handleParentOrganizationChange = (_, value) => {
    if (value === null || value.key == 'root')
      setOrganizationData({
        ...organizationData,
        parentOrganization: value,
        profile: null
      })
    else
      setOrganizationData({
        ...organizationData,
        parentOrganization: value
      })
  }

  const handleTimezoneChange = (_, value) => {
    setOrganizationData({
      ...organizationData,
      timezone: value
    })
  }

  const changeDomainAndKey = async (newOrgName) => {
    const keySuggestionResponse = await useFetchKeySuggestion(newOrgName)
    setOrganizationData({
      ...organizationData,
      domain: keySuggestionResponse.data.suggestion,
      organizationKey: keySuggestionResponse.data.suggestion
    })
  }

  const changeDomain = async (newOrgName) => {
    const keySuggestionResponse = await useFetchKeySuggestion(newOrgName)
    setOrganizationData({
      ...organizationData,
      domain: keySuggestionResponse.data.suggestion
    })
  }

  useEffect(() => {
    if (organizationData == null || organizationData.organizationName == '') {
      return
    }

    if (firstTime.current) {
      firstTime.current = false
      return
    }

    if (!organization) {
      const debounce = setTimeout(() => {
        changeDomainAndKey(organizationData.organizationName)
      }, 500)
      return () => clearTimeout(debounce)
    } else {
      const debounce = setTimeout(() => {
        changeDomain(organizationData.organizationName)
      }, 500)
      return () => clearTimeout(debounce)
    }
  }, [organizationData.organizationName])

  const handleNameChange = async (event) => {
    const newOrgName = event.target.value
    setOrganizationData({
      ...organizationData,
      organizationName: newOrgName
    })
  }

  const handleVatNumberChange = (event) => {
    setOrganizationData({
      ...organizationData,
      vatNumber: event.target.value
    })
  }

  const handleOrganizationKeyChange = (event) => {
    setOrganizationData({
      ...organizationData,
      organizationKey: event.target.value
    })
  }

  const handleDomainChange = (event) => {
    setOrganizationData({
      ...organizationData,
      domain: event.target.value
    })
  }

  const handleNetworkServerUserChange = (_, value) => {
    setOrganizationData({
      ...organizationData,
      networkServer: value
    })
  }

  const handleProfileChange = (_, value) => {
    setOrganizationData({
      ...organizationData,
      profile: value
    })
  }

  const handleUploadedFiles = (event) => {
    setOrganizationData({
      ...organizationData,
      file: event.target.files[0]
    })
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid
        py={2}
        container
        spacing={2}
      >
        <Grid
          size={6}
          py={1}
        >
          <Typography id="organization-form-root-organization-label">{t('organizations.root')}</Typography>
        </Grid>

        <Grid
          size={6}
          py={1}
        >
          {organization ? (
            <TextField
              variant="standard"
              disabled
              fullWidth
              value={organizationData?.parentOrganization?.name || 'root'}
            />
          ) : (
            <Autocomplete
              id="organization-form-root-organization"
              aria-labelledby="organization-form-root-organization-label"
              disabled={readonly}
              getOptionLabel={(option) => option.name}
              onChange={handleParentOrganizationChange}
              isOptionEqualToValue={(o1, o2) => o1.key === o2.key}
              fullWidth
              options={organizationsOptions || []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('common.choose')}
                  variant="standard"
                  error={validation?.messages?.parentOrganization?.length > 0}
                  helperText={validation?.messages?.parentOrganization?.join(
                    ', '
                  )}
                />
              )}
            />
          )}
        </Grid>

        <Grid
          size={6}
          py={1}
        >
          <Typography id="organization-form-timezone-label">{t('common.timezone')}</Typography>
        </Grid>

        <Grid
          size={6}
          py={1}
        >
          {timezones && (
            <Autocomplete
              id="organization-form-timezone"
              aria-labelledby="organization-form-timezone-label"
              value={organization?.timeZone || organizationData?.timezone || ''}
              loading={isTimezonesLoading}
              options={timezones}
              onChange={handleTimezoneChange}
              disabled={readonly}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('common.choose')}
                  variant="standard"
                  error={validation?.messages?.timezone?.length > 0}
                  helperText={validation?.messages?.timezone?.join(', ')}
                />
              )}
            />
          )}
        </Grid>

        <Grid size={12}>
          <Divider />
        </Grid>

        <Grid
          size={6}
          py={1}
        >
          <TextField
            id="organization-form-organization-name"
            required
            label={t('common.name')}
            variant="standard"
            fullWidth
            onChange={handleNameChange}
            value={organizationData?.organizationName || ''}
            error={validation?.messages?.organizationName?.length > 0}
            helperText={validation?.messages?.organizationName?.join(', ')}
            disabled={readonly}
          />
        </Grid>

        <Grid
          size={6}
          py={1}
        >
          <TextField
            label={t('organizations.vat')}
            variant="standard"
            fullWidth
            disabled={!!organization || readonly}
            value={organizationData?.vatNumber || ''}
            onChange={handleVatNumberChange}
          />
        </Grid>

        <Grid
          size={6}
          py={1}
        >
          <TextField
            required
            label={t('organizations.key')}
            variant="standard"
            fullWidth
            disabled={!!organization || readonly}
            value={
              organizationData?.organizationKey
                ? organizationData.organizationKey
                : ''
            }
            onChange={handleOrganizationKeyChange}
            error={validation?.messages?.organizationKey?.length > 0}
            helperText={validation?.messages?.organizationKey?.join(', ')}
          />
        </Grid>

        <Grid
          size={6}
          py={1}
        >
          <TextField
            required
            label={t('organizations.domain')}
            variant="standard"
            fullWidth
            value={organizationData?.domain ? organizationData.domain : ''}
            onChange={handleDomainChange}
            error={validation?.messages?.domain?.length > 0}
            helperText={validation?.messages?.domain?.join(', ')}
            disabled={readonly}
          />
        </Grid>

        <Grid size={12}>
          <Divider />
        </Grid>

        <Grid
          size={6}
          py={1}
        >
          <Typography>
            Network server:{' '}
            <strong>
              {!isNetworkServersLoading && networkServer
                ? networkServer.id
                : ''}
            </strong>
          </Typography>
        </Grid>

        <Grid
          size={6}
          py={1}
        >
          {organization ? (
            <TextField
              fullWidth={true}
              disabled
              label={t('common.choose')}
              variant="standard"
              value={organization.sourceData?.user || 'Nessuno'}
            />
          ) : (
            <Autocomplete
              id="organization-form-network-server"
              disabled={readonly}
              loading={isNetworkServersLoading}
              getOptionLabel={(option) => option.user}
              disableClearable={true}
              onChange={handleNetworkServerUserChange}
              isOptionEqualToValue={(o1, o2) => o1.userId == o2.userId}
              options={networkServerUserOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={`${t('common.choose')} nome utente network server`}
                  variant="standard"
                  error={validation?.messages?.networkServer?.length > 0}
                  helperText={validation?.messages?.networkServer?.join(', ')}
                />
              )}
            />
          )}
        </Grid>

        {organizationData?.parentOrganization?.key === 'root' && (
          <>
            <Grid
              size={6}
              py={1}
            >
              <Typography>{t('organizations.organizationProfile')}</Typography>
            </Grid>

            <Grid
              size={6}
              py={1}
            >
              <Autocomplete
                id="organization-form-profile"
                loading={isOrganizationProfilesLoading}
                disabled={readonly}
                getOptionLabel={(option) => option.name}
                onChange={handleProfileChange}
                options={organizationProfiles}
                value={organizationData.profile || { name: '' }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label={t('common.choose')}
                    variant="standard"
                    error={validation?.messages?.profile?.length > 0}
                    helperText={validation?.messages?.profile?.join(', ')}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Divider />
            </Grid>
          </>
        )}

        {!readonly ?
          <Grid
            display="flex"
            justifyContent="center"
            size={12}
          >
            <Card>
              <CardHeader
                title={
                  <Typography variant="title">
                    {t('organizations.loadLogo')}
                  </Typography>
                }
                subheader={
                  <Typography
                    component="p"
                    variant="body2"
                    color={'#626262'}
                  >
                    {t('organizations.maxSize')}
                  </Typography>
                }
              />
              <CardContent>
                <Stack
                  direction={'row'}
                  justifyContent={'center'}
                  spacing={3}
                  alignItems={'center'}
                >
                  <Button
                    id="organization-form-logo-upload"
                    sx={{ height: '38px' }}
                    component="label"
                    variant="contained"
                    aria-label={t('organizations.uploadLogo')}
                    startIcon={<CloudUploadRoundedIcon />}
                  >
                    {t('organizations.uploadLogo')}
                    <VisuallyHiddenInput
                      onChange={handleUploadedFiles}
                      type="file"
                    />
                  </Button>
                  {organizationData?.file && (
                    <Box
                      component="img"
                      sx={{ height: '100px' }}
                      src={URL.createObjectURL(organizationData.file)}
                    />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          : ''}
      </Grid>
    </Box>
  )
}

export default OrganizationForm
